// Ascendance verticale (pedigree) en SVG natif : personne racine en bas,
// ascendants empilés vers le haut, connecteurs orthogonaux. forwardRef sur le
// <svg> pour l'export.

import React, { forwardRef } from 'react';
import { Membre, Relation } from '../../types';
import { getAncestors, AncestorNode } from '../../utils/lineage';
import { StylePreset, resolveBg, PersonBox } from './chartCommon';

export interface AscendanceChartProps {
  rootId: number;
  membres: Membre[];
  relations: Relation[];
  style: StylePreset;
  isDark: boolean;
  maxGen: number;
}

const BOX_W = 132, BOX_H = 46, COL_GAP = 16, ROW_GAP = 46, PAD = 18;

function collect(node: AncestorNode | null, out: AncestorNode[]) {
  if (!node) return;
  out.push(node);
  collect(node.pere, out);
  collect(node.mere, out);
}

const AscendanceChart = forwardRef<SVGSVGElement, AscendanceChartProps>(function AscendanceChart(
  { rootId, membres, relations, style, isDark, maxGen }, ref,
) {
  const root = getAncestors(rootId, membres, relations, maxGen);
  const all: AncestorNode[] = [];
  collect(root, all);

  // Layout en colonnes : les ascendants sans parents (ou au max) prennent une
  // colonne ; chaque enfant est centré sous ses parents.
  const colOf = new Map<AncestorNode, number>();
  let nextCol = 0;
  (function layout(node: AncestorNode | null): number {
    if (!node) return 0;
    if (node.gen >= maxGen || (!node.pere && !node.mere)) {
      const c = nextCol++;
      colOf.set(node, c);
      return c;
    }
    const cols: number[] = [];
    if (node.pere) cols.push(layout(node.pere));
    if (node.mere) cols.push(layout(node.mere));
    const c = cols.reduce((a, b) => a + b, 0) / cols.length;
    colOf.set(node, c);
    return c;
  })(root);

  const maxCol = all.length ? Math.max(...all.map(n => colOf.get(n) ?? 0)) : 0;
  const colStep = BOX_W + COL_GAP;
  const W = maxCol * colStep + BOX_W + PAD * 2;
  const H = maxGen * (BOX_H + ROW_GAP) + BOX_H + PAD * 2;
  const xOf = (n: AncestorNode) => PAD + (colOf.get(n) ?? 0) * colStep;
  const yOf = (n: AncestorNode) => PAD + (maxGen - n.gen) * (BOX_H + ROW_GAP);

  const edges: React.ReactNode[] = [];
  const boxes: React.ReactNode[] = [];

  all.forEach((n, i) => {
    const x = xOf(n), y = yOf(n);
    [n.pere, n.mere].forEach((p, pi) => {
      if (!p) return;
      const childX = x + BOX_W / 2, childY = y;             // haut de l'enfant
      const parentX = xOf(p) + BOX_W / 2, parentY = yOf(p) + BOX_H; // bas du parent
      const midY = (childY + parentY) / 2;
      edges.push(
        <path key={`e${i}-${pi}`}
          d={`M ${childX} ${childY} L ${childX} ${midY} L ${parentX} ${midY} L ${parentX} ${parentY}`}
          fill="none" stroke={style.edge} strokeWidth={style.edgeW} strokeLinecap="round" />,
      );
    });
    boxes.push(
      <PersonBox key={`b${i}`} x={x} y={y} w={BOX_W} h={BOX_H}
        membre={n.membre} gen={n.gen} style={style} emphasize={n.gen === 0} />,
    );
  });

  const bg = resolveBg(style, isDark);
  return (
    <svg ref={ref} width={Math.ceil(W)} height={Math.ceil(H)} viewBox={`0 0 ${Math.ceil(W)} ${Math.ceil(H)}`}
      xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Ascendance">
      <rect x={0} y={0} width={Math.ceil(W)} height={Math.ceil(H)} fill={bg} />
      {edges}
      {boxes}
    </svg>
  );
});

export default AscendanceChart;
